import { LockOutlined, StopOutlined } from '@ant-design/icons';

import { Checkbox, Skeleton } from '../../../components/ui';
import { useT } from '../../../i18n';
import { LY_DO_TAT, TEN_BUOC, TEN_NHOM_BUOC } from '../constants';
import { batTatDuoc, gomTheoNhom } from '../nghiep-vu/cau-hinh';
import type { BuocDanhMuc, BuocHieuLuc, NhomBuoc } from '../types';

/**
 * Các bước của quy trình, gom theo nhóm như màn v3 (ảnh người dùng cung cấp 24/10/2026), theo **thứ tự chạy thật**.
 *
 * Bỏ phần kéo thả khối của v3: chính màn v3 ghi "Vị trí khối chỉ để nhìn. Thứ tự chạy do máy chủ cưỡng chế và không
 * đổi được bằng cách kéo" — một thao tác kéo không đổi được gì là lời mời hiểu sai.
 *
 * Bốn dạng của một bước, mỗi dạng nói một điều khác nhau:
 *
 * - **Khoá** (khoá nhỏ, luôn tích): tắt nó là bỏ lý do tồn tại của phiên định danh.
 * - **Chưa khả dụng** (mờ, biểu tượng cấm, **không bao giờ tích**): phân hệ chưa có ở bản này. Hiện để người dùng
 *   biết hệ thống *sẽ* làm được; không tích, không cho bấm, vì một bước an ninh "đang bật" mà không chạy tệ hơn hẳn
 *   một bước tắt (`08` §6). Bước mặc định bật thì nói thêm rằng nó tự chạy khi phân hệ có.
 * - **Tự bật**: bật vì một bước khác cần nó, không vì người cấu hình chọn.
 * - **Thiếu phụ thuộc**: người cấu hình muốn bật, nhưng bước nó cần đang tắt — máy chủ báo lỗi ở bước ấy.
 */
export function KhoiBuocQuyTrinh({
  buoc,
  danhMuc,
  nhom,
  suaDuoc,
  loi,
  onDoi,
}: {
  /** Bản hiệu lực từ máy chủ; `null` khi lượt xem trước đầu tiên chưa về. */
  buoc: BuocHieuLuc[] | null;
  danhMuc: readonly BuocDanhMuc[];
  nhom: readonly NhomBuoc[];
  suaDuoc: boolean;
  /** Lỗi theo con trỏ — bước nào có `#/buoc/<mã>` thì hiện ngay dưới bước ấy. */
  loi: Record<string, string>;
  onDoi: (ma: string, bat: boolean) => void;
}) {
  const { t } = useT();

  if (!buoc) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  const tenBuoc = (ma: string) => (TEN_BUOC[ma] ? t(TEN_BUOC[ma]) : ma);
  const theoMa = new Map(danhMuc.map((b) => [b.ma, b]));

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {gomTheoNhom(buoc, nhom).map((g, i) => (
        <section key={g.nhom} aria-labelledby={`nhom-${g.nhom}`} className="rounded-lg border border-default bg-subtle p-3">
          <h3 id={`nhom-${g.nhom}`} className="mb-2 flex items-center gap-2 text-sm font-semibold text-content-primary">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-2xs text-primary-700">
              {i + 1}
            </span>
            <span className="min-w-0 truncate">{t(TEN_NHOM_BUOC[g.nhom])}</span>
          </h3>
          <ul className="space-y-1.5">
            {g.buoc.map((b) => {
              const chuaCo = !b.khaDung;
              const loiBuoc = loi[`#/buoc/${b.ma}`];
              const can = theoMa.get(b.ma)?.can ?? [];
              let phu: string | null = null;
              // "Chưa khả dụng" đứng trước "Luôn bật": bước khoá mà chưa có phân hệ thì vẫn chưa chạy.
              if (chuaCo) phu = b.yeuCau ? t('ekyc.quyTrinh.chuaKhaDungSeChay') : t(LY_DO_TAT.chua_kha_dung);
              else if (b.khoa) phu = t('ekyc.quyTrinh.luonBat');
              else if (b.tuBat) phu = t('ekyc.quyTrinh.tuBat');
              else if (b.lyDoTat === 'thieu_phu_thuoc') {
                phu = t('ekyc.quyTrinh.canBuoc', { ten: can.map(tenBuoc).join(', ') });
              }
              return (
                <li
                  key={b.ma}
                  className={`rounded border px-2.5 py-2 ${chuaCo ? 'border-dashed border-default bg-surface opacity-70' : 'border-default bg-surface'}`}
                >
                  <Checkbox
                    // Bước chưa khả dụng không bao giờ hiện là đã tích: ô tích đọc thành "đang chạy".
                    checked={!chuaCo && (b.yeuCau || b.tuBat)}
                    disabled={!suaDuoc || !batTatDuoc(b) || b.tuBat}
                    onChange={(e) => onDoi(b.ma, e.target.checked)}
                  >
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-content-primary">
                      {b.khoa && !chuaCo && <LockOutlined className="text-content-muted" aria-hidden />}
                      {chuaCo && <StopOutlined className="text-content-muted" aria-hidden />}
                      <span className="min-w-0">{tenBuoc(b.ma)}</span>
                    </span>
                  </Checkbox>
                  {phu && <p className="mt-0.5 pl-6 text-2xs text-content-muted">{phu}</p>}
                  {loiBuoc && (
                    <p role="alert" className="mt-0.5 pl-6 text-2xs text-danger-700">
                      {loiBuoc}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
